import { Controller } from "@hotwired/stimulus"

// 비동기 PDF 다운로드.
//
// 사용 예시 (view):
//   <button data-controller="pdf-download"
//           data-pdf-download-kind-value="warning_label"
//           data-pdf-download-code-value="<%= @product.code %>"
//           data-action="click->pdf-download#start">PDF 다운로드</button>
//   <%= render 'shared/pdf_download_modal' %>
//
// click → POST /workplace/pdf_jobs → 모달 open → 1초 폴링 → completed 시
// hidden <a> click 으로 다운로드 → 모달 close.
export default class extends Controller {
  static values = {
    kind: String,
    code: String,
    pollInterval: { type: Number, default: 1000 },
    maxAttempts: { type: Number, default: 60 },  // 60 × 1s = 60s 타임아웃
    preview: { type: Boolean, default: false }   // true 면 새 탭에서 PDF preview, false 면 다운로드
  }

  async start(event) {
    if (event) event.preventDefault()
    if (this.busy) return
    this.busy = true
    this.toggleTriggers(false)

    try {
      this.openModal()
      this.showSpinner("PDF 생성 요청 중...")

      const csrf = this.csrfToken()
      const res = await fetch("/workplace/pdf_jobs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept": "application/json"
        },
        body: JSON.stringify({ kind: this.kindValue, code: this.codeValue })
      })
      const json = await res.json()
      if (!res.ok || !json.success) {
        throw new Error(this.errorLabel(json) || "PDF 생성 요청에 실패했습니다.")
      }

      this.showSpinner("PDF 생성 중... 잠시만 기다려 주세요.")
      await this.poll(json.data.status_url, json.data.download_url, 0)
    } catch (e) {
      this.showError(e.message)
    } finally {
      this.busy = false
      this.toggleTriggers(true)
    }
  }

  async poll(statusUrl, downloadUrl, attempt) {
    if (attempt >= this.maxAttemptsValue) {
      throw new Error("PDF 생성 시간이 초과되었습니다.")
    }
    await this.sleep(this.pollIntervalValue)

    const res = await fetch(statusUrl, {
      headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken() }
    })
    const json = await res.json()
    if (!res.ok || !json.success) {
      throw new Error(this.errorLabel(json) || "상태 조회에 실패했습니다.")
    }

    const { status, error } = json.data
    if (status === "completed") {
      if (this.previewValue) {
        // 막혔으면 모달을 닫지 않는다 — 닫으면 사용자에게는 버튼이 먹통으로 보인다.
        if (!this.openPreview(downloadUrl)) {
          this.showBlocked(this.previewUrl(downloadUrl))
          return
        }
      } else {
        this.triggerDownload(downloadUrl)
      }
      this.closeModal()
      return
    }
    if (status === "failed") {
      throw new Error(error || "PDF 생성에 실패했습니다.")
    }
    return this.poll(statusUrl, downloadUrl, attempt + 1)
  }

  triggerDownload(url) {
    // Turbo 가 가로채면 fetch + native navigation 으로 download 가 2번 호출되어
    // 첫 호출에서 cache 가 삭제되고 두 번째에서 404 발생. data-turbo=false 로 차단.
    const a = document.createElement("a")
    a.href = url
    a.setAttribute("data-turbo", "false")
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  // ?disposition=inline 이면 백엔드가 'inline' 으로 응답 → 브라우저 PDF viewer 가 렌더.
  previewUrl(url) {
    const sep = url.includes("?") ? "&" : "?"
    return `${url}${sep}disposition=inline`
  }

  // 새 탭을 열었으면 true, 팝업 차단에 막혔으면 false.
  //
  // window.open 은 사용자 제스처 직후에만 허용되는데(Chrome 기준 5초), 여기는 클릭이
  // 아니라 폴링 루프 안이다. PDF 생성이 그 창을 넘기면 조용히 막히고, 서버가 붐빌 때만
  // 실패하는 형태로 나타난다. Safari 는 제스처 핸들러 밖이면 시간과 무관하게 막는다.
  // 막힌 경우 window.open 은 null 을 준다 — 예전에는 이 값을 보지 않았다.
  openPreview(url) {
    const win = window.open(this.previewUrl(url), "_blank")
    return !!win
  }

  // ── 모달 제어 ──────────────────────────────────────────────
  modalElement() {
    return document.getElementById("pdf-download-modal")
  }

  openModal() {
    const modal = this.modalElement()
    if (!modal) return
    modal.dispatchEvent(new CustomEvent("modal:open"))
  }

  closeModal() {
    const modal = this.modalElement()
    if (!modal) return
    const ctrl = this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (ctrl) ctrl.close()
  }

  // ── 모달 본문 영역 토글 ────────────────────────────────────
  // 패널 셋(spinner·blocked·error)은 서로 배타적이다. 각자 토글하면 하나가 남는다.
  showPane(name) {
    const modal = this.modalElement()
    if (!modal) return null

    ;["spinner", "blocked", "error"].forEach((pane) => {
      const el = modal.querySelector(`[data-pdf-download-modal='${pane}']`)
      if (el) el.classList.toggle("hidden", pane !== name)
    })
    return modal
  }

  showBlocked(url) {
    const modal = this.showPane("blocked")
    if (!modal) return
    const link = modal.querySelector("[data-pdf-download-modal='blocked-link']")
    if (link) link.href = url
  }

  showSpinner(msg) {
    const modal = this.showPane("spinner")
    if (!modal) return
    const status = modal.querySelector("[data-pdf-download-modal='status']")
    if (status) status.textContent = msg
  }

  showError(msg) {
    const modal = this.showPane("error")
    if (!modal) return
    const errorMessage = modal.querySelector("[data-pdf-download-modal='error-message']")
    if (errorMessage) errorMessage.textContent = msg
  }

  // ── 유틸 ─────────────────────────────────────────────────
  toggleTriggers(enabled) {
    const triggers = document.querySelectorAll("[data-controller~='pdf-download']")
    triggers.forEach((el) => {
      if (enabled) el.removeAttribute("disabled")
      else el.setAttribute("disabled", "true")
    })
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  errorLabel(json) {
    if (!json) return null
    return json.error || json.message
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}
